import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Store, Briefcase } from "lucide-react";
import { trpcClient } from "@/trpc/client";
import { cn } from "@/lib/utils";

/**
 * "Vilken typ av verksamhet?" (GRA-87): a sales organisation or a grocery
 * store. Saved immediately, because it changes the next steps (a store has
 * no integrations step) and the words used everywhere after.
 */
export function BusinessVerticalChoice({
  businessId,
  value,
  onChange,
}: {
  businessId: string | null;
  value: "sales" | "dvh";
  onChange: (v: "sales" | "dvh") => void;
}) {
  const { t } = useTranslation("onboarding");
  const [saving, setSaving] = useState(false);

  async function choose(next: "sales" | "dvh") {
    if (!businessId || next === value || saving) return;
    const previous = value;
    onChange(next);
    setSaving(true);
    try {
      await trpcClient.business.setVertical.mutate({ businessId, vertical: next });
    } catch {
      onChange(previous);
    } finally {
      setSaving(false);
    }
  }

  const options = [
    { key: "sales" as const, icon: Briefcase },
    { key: "dvh" as const, icon: Store },
  ];
  return (
    <div className="mb-6 space-y-2">
      <p className="text-sm font-medium">{t("vertical.question")}</p>
      <div role="radiogroup" className="grid gap-2 sm:grid-cols-2">
        {options.map(({ key, icon: Icon }) => (
          <button
            key={key}
            type="button"
            role="radio"
            aria-checked={value === key}
            disabled={saving}
            onClick={() => void choose(key)}
            className={cn(
              "flex items-start gap-3 rounded-lg border p-3 text-left transition-colors",
              value === key ? "border-primary bg-primary/5" : "hover:bg-accent",
            )}
          >
            <Icon className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
            <span>
              <span className="block text-sm font-medium">{t(`vertical.${key}.title`)}</span>
              <span className="block text-xs text-muted-foreground">{t(`vertical.${key}.description`)}</span>
            </span>
          </button>
        ))}
      </div>
    </div>
  );
}
